import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { escapeIlike } from '../lib/searchUtils'
import { googleMapsUrl } from '../lib/mapUtils'
import PropertyJobsModal from '../components/PropertyJobsModal'

const PAGE_SIZE = 50

export default function PropertyHistory() {
  const [properties, setProperties] = useState([])
  const [jobCounts, setJobCounts] = useState({})
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [searchParams] = useSearchParams()

  // Auto-open property from URL param (e.g. ?propertyId=xxx from job details modal)
  useEffect(() => {
    const propertyId = searchParams.get('propertyId')
    if (!propertyId || !supabase) return
    supabase
      .from('properties')
      .select('*')
      .eq('id', propertyId)
      .single()
      .then(({ data }) => { if (data) setSelectedProperty(data) })
  }, [searchParams])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(0) // Reset to first page on new search
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const load = useCallback(async (isMounted) => {
    if (!supabase) return
    setLoading(true)
    try {
      // Build query
      let query = supabase
        .from('properties')
        .select('*', { count: 'exact' })
        .neq('is_deleted', true)

      // Add search filter (server-side)
      if (debouncedSearch) {
        const searchTerm = `%${escapeIlike(debouncedSearch)}%`
        query = query.or(`name.ilike.${searchTerm},address.ilike.${searchTerm}`)
      }

      // Add pagination and ordering
      const from = page * PAGE_SIZE
      const to = from + PAGE_SIZE - 1
      query = query.order('name').range(from, to)

      const { data: propsData, error: propsError, count } = await query

      if (!propsError && propsData && isMounted.current) {
        setProperties(propsData)
        setTotalCount(count || 0)

        // Load job counts for displayed properties only
        if (propsData.length > 0) {
          const propertyIds = propsData.map(p => p.id)

          // Prefer the aggregated RPC (one row per property). Falls back to
          // the legacy row-by-row count if the function isn't installed yet.
          const { data: rpcData, error: rpcError } = await supabase
            .rpc('get_property_job_counts', { property_ids: propertyIds })

          if (!rpcError && rpcData && isMounted.current) {
            const counts = {}
            rpcData.forEach(row => { counts[row.property_id] = Number(row.job_count) })
            setJobCounts(counts)
          } else {
            const { data: jobsData, error: jobsError } = await supabase
              .from('jobs')
              .select('property_id')
              .in('property_id', propertyIds)
              .neq('is_deleted', true)

            if (!jobsError && jobsData && isMounted.current) {
              const counts = {}
              jobsData.forEach(job => {
                if (job.property_id) {
                  counts[job.property_id] = (counts[job.property_id] || 0) + 1
                }
              })
              setJobCounts(counts)
            }
          }
        } else {
          setJobCounts({})
        }
      }
    } catch (e) {
      console.warn('Could not load properties:', e.message)
    } finally {
      if (isMounted.current) setLoading(false)
    }
  }, [debouncedSearch, page])

  useEffect(() => {
    const isMounted = { current: true }
    load(isMounted)
    return () => { isMounted.current = false }
  }, [load])

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Property History</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {debouncedSearch
              ? `${totalCount} result${totalCount !== 1 ? 's' : ''} for "${debouncedSearch}"`
              : `${totalCount} properties`
            }
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search properties..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      {/* Property List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <svg className="animate-spin w-6 h-6 text-brand-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : properties.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-sm">{debouncedSearch ? 'No properties match your search' : 'No properties yet'}</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <ul className="divide-y divide-gray-100">
            {properties.map(property => {
              const count = jobCounts[property.id] || 0
              return (
                <li
                  key={property.id}
                  onClick={() => setSelectedProperty(property)}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    property.do_not_service ? 'bg-red-50 hover:bg-red-100' : property.past_due_invoice ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-medium truncate ${property.do_not_service ? 'text-red-700' : property.past_due_invoice ? 'text-orange-700' : 'text-gray-900'}`}>{property.name}</h3>
                        {property.do_not_service && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 flex-shrink-0">⛔ DNS</span>
                        )}
                        {property.past_due_invoice && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 flex-shrink-0">⚠️ PDI</span>
                        )}
                      </div>
                      <p className={`text-sm truncate ${property.do_not_service ? 'text-red-600' : property.past_due_invoice ? 'text-orange-600' : 'text-gray-500'}`}>{property.address}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {property.address && (
                        <a
                          href={googleMapsUrl(property.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="Open in Google Maps"
                          className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </a>
                      )}
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        count > 0 ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {count} job{count !== 1 ? 's' : ''}
                      </span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Property Jobs Modal */}
      <PropertyJobsModal
        isOpen={!!selectedProperty}
        onClose={() => setSelectedProperty(null)}
        property={selectedProperty}
      />
    </div>
  )
}
